import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useImpersonation } from '../context/ImpersonationContext';
import { canModule } from '../config/permissions';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Everyone real is eligible to be viewed-as — the old client-facing-only
// filter hid receptionists, coordinators, and ops-only users (Riley, Jessica
// Kelly). Only test/system accounts are excluded.
const SYSTEM_ACCOUNT_RE = /\btest\b|_mgr|receptionless|ll dual|cutie pie wax bar/i;

function isViewable(emp) {
  const name = `${emp.firstName || ''} ${emp.lastName || ''} ${emp.displayName || ''}`;
  if (!name.trim()) return false;
  return !SYSTEM_ACCOUNT_RE.test(name);
}

// Map jobTitle/jobName to the ROLE_MODULES role key that drives sidebar
// visibility. Lead matches before generic so a "Lead Waxer" doesn't fall
// through to esthetician. Used only when the person has no OpsUser role.
function jobToRole(emp) {
  const blob = `${emp.jobTitle || ''} ${emp.jobName || ''}`.toLowerCase();
  if (blob.includes('lead waxer')) return 'lead_waxer';
  if (blob.includes('lead nail')) return 'lead_nail_tech';
  if (blob.includes('nail')) return 'nail_tech';
  if (blob.includes('esthetician') || blob.includes('waxer')) return 'esthetician';
  if (blob.includes('coordinator')) return 'salon_coordinator';
  if (blob.includes('receptionist') || blob.includes('front')) return 'receptionist';
  return 'employee';
}

export default function ViewAsSelector({ session }) {
  const { impersonatedAs, isImpersonating, startImpersonating, stopImpersonating } = useImpersonation();

  const [open, setOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  // therapistId → { isAdmin, permissions } so view-as previews the target's
  // full admin overlay, not just their role.
  const [opsUserMap, setOpsUserMap] = useState({});
  const ref = useRef(null);

  // Owner always; others need the `view_as` toggle (Settings → Team).
  // Mirrors the backend middleware gate on X-Impersonate-User-Id.
  const canViewAs = canModule(session, 'view_as');

  useEffect(() => {
    if (!open || employees.length > 0 || loading) return;
    setLoading(true);
    Promise.all([
      fetch(`${API_URL}/api/ops/dashboard/employees`, {
        headers: { Authorization: `Bearer ${session.opsToken}` },
      }).then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`${API_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${session.opsToken}` },
      }).then(r => r.ok ? r.json() : []).catch(() => []),
    ])
      .then(([list, opsUsers]) => {
        const filtered = (list || [])
          .filter(e => e.isActive !== false)
          .filter(isViewable)
          .sort((a, b) => (a.firstName || '').localeCompare(b.firstName || ''));
        setEmployees(filtered);
        const map = {};
        for (const u of (opsUsers || [])) {
          if (u.therapistId) {
            map[u.therapistId] = { isAdmin: !!u.isAdmin, permissions: u.permissions || [], modules: u.modules || [] };
          }
        }
        setOpsUserMap(map);
      })
      .finally(() => setLoading(false));
  }, [open, employees.length, loading, session?.opsToken]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return employees;
    const q = search.toLowerCase();
    return employees.filter(e => {
      const name = `${e.firstName || ''} ${e.lastName || ''}`.toLowerCase();
      return name.includes(q);
    });
  }, [employees, search]);

  if (!canViewAs) return null;

  function handleSelect(emp) {
    const overlay = opsUserMap[emp.id] || {};
    startImpersonating({
      employeeId: emp.id,
      employeeName: `${emp.firstName || ''} ${emp.lastName || ''}`.trim(),
      centerId: emp.centerId,
      // Prefer canonical OpsUser.role from the employees endpoint; fall
      // back to jobToRole heuristic if the OpsUser link is missing.
      role: emp.opsRole || jobToRole(emp),
      // Admin overlay so the preview matches what the user actually sees,
      // including admin-only items (Settings) and granular toggles.
      isAdmin: !!overlay.isAdmin,
      permissions: overlay.permissions || [],
      // Effective modules from /api/admin/users — the sidebar gates on
      // session.modules, so without these View As showed only Tasks.
      modules: overlay.modules || [],
    });
    setOpen(false);
    setSearch('');
    // Reload so all components pick up the new effective session cleanly.
    // App.js initializes staffView to 'tasks' on mount — visible to every
    // ROLE_MODULES role — so this also handles the "I was on /payroll when
    // I clicked View As" redirect for free.
    window.location.reload();
  }

  function handleExit() {
    stopImpersonating();
    setOpen(false);
    window.location.reload();
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        title={isImpersonating ? `Viewing as ${impersonatedAs.employeeName}` : 'View as another staff member'}
        style={{
          fontSize: 12,
          color: isImpersonating ? '#b5556a' : '#6B7280',
          background: isImpersonating ? '#fdf0f4' : 'none',
          border: `1px solid ${isImpersonating ? '#f0c4d2' : '#E5E7EB'}`,
          borderRadius: 8,
          padding: '5px 10px',
          cursor: 'pointer',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span aria-hidden>👁</span>
        {isImpersonating ? `Viewing as ${impersonatedAs.employeeName}` : 'View as'}
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 6px)',
          right: 0,
          width: 280,
          background: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: 10,
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
          zIndex: 60,
          padding: 8,
          maxHeight: 420,
          display: 'flex',
          flexDirection: 'column',
        }}>
          {isImpersonating && (
            <button
              onClick={handleExit}
              style={{
                fontSize: 12,
                color: '#b5556a',
                background: '#fdf0f4',
                border: 'none',
                borderRadius: 6,
                padding: '8px 10px',
                marginBottom: 8,
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
              }}
            >
              ✕ Exit "View as {impersonatedAs.employeeName}"
            </button>
          )}

          <input
            type="text"
            placeholder="Search staff..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
            style={{
              fontSize: 13,
              padding: '6px 8px',
              border: '1px solid #E5E7EB',
              borderRadius: 6,
              marginBottom: 6,
              fontFamily: 'Inter, sans-serif',
            }}
          />

          <div style={{ overflowY: 'auto', flex: 1 }}>
            {loading && <div style={{ padding: 10, fontSize: 12, color: '#9CA3AF' }}>Loading...</div>}
            {!loading && filtered.length === 0 && (
              <div style={{ padding: 10, fontSize: 12, color: '#9CA3AF' }}>
                {employees.length === 0 ? 'No client-facing staff found.' : 'No match.'}
              </div>
            )}
            {!loading && filtered.map(emp => {
              const isCurrent = impersonatedAs?.employeeId === emp.id;
              return (
                <button
                  key={emp.id}
                  onClick={() => handleSelect(emp)}
                  disabled={isCurrent}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '7px 8px',
                    fontSize: 13,
                    background: isCurrent ? '#fdf0f4' : 'transparent',
                    color: isCurrent ? '#b5556a' : '#374151',
                    border: 'none',
                    borderRadius: 6,
                    cursor: isCurrent ? 'default' : 'pointer',
                    fontFamily: 'Inter, sans-serif',
                  }}
                  onMouseEnter={e => { if (!isCurrent) e.currentTarget.style.background = '#F9FAFB'; }}
                  onMouseLeave={e => { if (!isCurrent) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ fontWeight: 500 }}>{emp.firstName} {emp.lastName}</div>
                  <div style={{ fontSize: 11, color: '#9CA3AF' }}>{emp.jobTitle || emp.jobName || ''}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
